import { Component, computed, effect, input, signal } from '@angular/core';
import { FormArray, FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  IconComponent,
  InputComponent,
  TooltipComponent,
  TypographyComponent,
} from '@purolator/ui-kit-angular';
import { TranslatePipe } from '../../../../core/i18n';
import { AlertToast } from '../../../../shared/components/alert-toast/alert-toast';
import { customerPieceFieldHasError } from '../../customer-piece-field';
import { TRACKING_REFERENCE_MAX_LENGTH } from '../../package-types.config';
import { TRACKING_REFERENCE_VALIDATION_ERROR_KEYS } from '../../validators/tracking-reference.validators';

/**
 * Single optional tracking reference field (max length + restricted-character validation).
 */
@Component({
  selector: 'app-tracking-references-section',
  imports: [
    ReactiveFormsModule,
    AlertToast,
    IconComponent,
    InputComponent,
    TooltipComponent,
    TypographyComponent,
    TranslatePipe,
  ],
  templateUrl: './tracking-references-section.html',
  styleUrl: './tracking-references-section.scss',
})
export class TrackingReferencesSection {
  readonly trackingReferences = input.required<FormArray<FormControl<string>>>();
  /** When true, shows field errors and the restricted-characters banner after Save. */
  readonly showValidationErrors = input(false);

  protected readonly maxLength = TRACKING_REFERENCE_MAX_LENGTH;

  /**
   * Bumps when tracking values change or a field is blurred so banner/field
   * error state re-reads FormControl validity (controls are not signals).
   */
  private readonly validationRevision = signal(0);

  constructor() {
    effect((onCleanup) => {
      const refs = this.trackingReferences();
      this.showValidationErrors();
      this.validationRevision.update((value) => value + 1);

      const subscription = refs.valueChanges.subscribe(() => {
        this.validationRevision.update((value) => value + 1);
      });

      onCleanup(() => subscription.unsubscribe());
    });
  }

  /**
   * Whether any tracking reference has a restricted-character error after blur or submit.
   */
  protected readonly hasRestrictedCharactersError = computed(() => {
    this.validationRevision();
    const showErrors = this.showValidationErrors();

    return this.trackingReferences().controls.some(
      (control) =>
        customerPieceFieldHasError(control, showErrors) &&
        control.hasError(TRACKING_REFERENCE_VALIDATION_ERROR_KEYS.restrictedCharacters),
    );
  });

  /**
   * Per-index field error flags derived from validation revision + control state.
   */
  protected readonly fieldErrorFlags = computed(() => {
    this.validationRevision();
    const showErrors = this.showValidationErrors();

    return this.trackingReferences().controls.map((control) =>
      customerPieceFieldHasError(control, showErrors),
    );
  });

  /**
   * Returns whether a tracking reference control should show an error state.
   */
  protected hasFieldError(index: number): boolean {
    return this.fieldErrorFlags()[index] ?? false;
  }

  /**
   * Marks a tracking reference touched on blur so inline validation can display without Save.
   */
  protected onFieldBlur(index: number): void {
    this.trackingReferences().at(index)?.markAsTouched();
    this.validationRevision.update((value) => value + 1);
  }
}
